import Lenis from 'lenis';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { SplitText } from 'gsap/SplitText';

gsap.registerPlugin(ScrollTrigger, SplitText);

export function initScroll() {
    // Lenis drives native window scroll, so ScrollTrigger reads it directly;
    // it only needs to be told when Lenis moves. `anchors` smooth-scrolls the
    // in-page links (hero index, side nav).
    const lenis = new Lenis({ lerp: 0.1, anchors: true });

    gsap.ticker.add((time) => lenis.raf(time * 1000));
    gsap.ticker.lagSmoothing(0);

    lenis.on('scroll', ScrollTrigger.update);

    return lenis;
}

export function initScrollProgress() {
    const fill = document.getElementById('scrollProgressFill');
    if (!fill) return;

    ScrollTrigger.create({
        start: 0,
        end: 'max',
        onUpdate({ progress }) {
            fill.style.transform = `scaleY(${progress})`;
        },
    });
}

/*
 * The active section is the one crossing the middle of the viewport. It tints
 * the backdrop and progress bar (body[data-active] in CSS) and marks the side
 * nav. Runs with reduced motion too: it only swaps a colour.
 */
export function initActiveSection() {
    const links = document.querySelectorAll('.universe-nav__link');
    const sections = document.querySelectorAll('[data-universe]');

    // Fires whenever a section crosses the middle line; the active one is then
    // the last section that starts above it (also right for jumps and the footer)
    const io = new IntersectionObserver(() => {
        const middle = window.innerHeight / 2;
        let active = sections[0];
        sections.forEach(section => {
            if (section.getBoundingClientRect().top <= middle) active = section;
        });
        const id = active.dataset.universe;
        document.body.dataset.active = id;
        links.forEach(link => {
            if (link.dataset.target === id) link.setAttribute('aria-current', 'true');
            else link.removeAttribute('aria-current');
        });
    }, { rootMargin: '-50% 0px -50% 0px', threshold: 0 });

    sections.forEach(section => io.observe(section));
}

/*
 * One entrance for every section: headline lines rise out of a mask, then the
 * copy, cards and actions follow. Cards reveal in batches as they arrive, so
 * long grids don't wait for the whole section.
 */
export function initReveals() {
    const ease = 'expo.out';

    // Lines are re-split when fonts load or the width changes; onSplit rebuilds
    // the tween for the new lines and SplitText carries its progress over.
    function revealLines(headline, vars) {
        if (!headline) return;
        SplitText.create(headline, {
            type: 'lines',
            linesClass: 'line',
            mask: 'lines',
            autoSplit: true,
            onSplit: self => gsap.from(self.lines, {
                yPercent: 120, duration: 1.2, stagger: 0.08, ease, ...vars,
                scrollTrigger: vars.scrollTrigger && { ...vars.scrollTrigger },
            }),
        });
    }

    const hero = document.querySelector('.services-hero__content');
    if (hero) {
        revealLines(hero.querySelector('h1'), { delay: 0.25, duration: 1.3 });
        gsap.timeline({ delay: 0.15, defaults: { ease } })
            .from(hero.querySelectorAll('.services-hero__crumbs, .services-hero__eyebrow'), { autoAlpha: 0, y: 16, duration: 1, stagger: 0.08 }, 0)
            // __more: the trust strip on service pages
            .from(hero.querySelectorAll('.services-hero__lead, .services-hero__actions, .services-hero__more'), {
                autoAlpha: 0, y: 24, duration: 1.1, stagger: 0.1,
            }, 0.45);
        const index = document.querySelectorAll('.services-index li');
        if (index.length) gsap.from(index, { autoAlpha: 0, y: 12, duration: 0.9, stagger: 0.05, delay: 0.85, ease });
    }

    document.querySelectorAll('.universe-intro, .services-contact__inner').forEach(intro => {
        const scrollTrigger = { trigger: intro, start: 'top 82%', once: true };
        revealLines(intro.querySelector('h2'), { scrollTrigger });

        const rest = intro.querySelectorAll('.universe-tagline, .universe-body, .services-contact__lead, .s-btn');
        if (rest.length) {
            gsap.from(rest, { autoAlpha: 0, y: 20, duration: 1, stagger: 0.08, delay: 0.1, ease, scrollTrigger: { ...scrollTrigger } });
        }
    });

    const items = '.service-card, .pipeline__step, .universe-actions, .faq details, [data-reveal]';
    gsap.set(items, { autoAlpha: 0, y: 32 });
    ScrollTrigger.batch(items, {
        start: 'top 90%',
        once: true,
        onEnter: batch => gsap.to(batch, {
            autoAlpha: 1, y: 0, duration: 1, ease, stagger: 0.08, overwrite: true,
            clearProps: 'transform',
        }),
    });
}

/*
 * Cards light up under the pointer. The position goes into CSS variables, so
 * the glow itself is a compositor-friendly gradient in the stylesheet.
 */
export function initCardSpotlight() {
    document.querySelectorAll('.universe-cards').forEach(grid => {
        grid.addEventListener('pointermove', e => {
            const card = e.target.closest('.service-card');
            if (!card) return;
            const rect = card.getBoundingClientRect();
            card.style.setProperty('--mx', `${e.clientX - rect.left}px`);
            card.style.setProperty('--my', `${e.clientY - rect.top}px`);
        });
    });
}
